/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { getEmbeddedResources, StateResource } from '@common/tech-shared';
import { inject, Injectable } from '@angular/core';
import { InstantSearchResult } from '@ods/system';
import { isNull } from 'lodash-es';
import { map, Observable } from 'rxjs';
import { ZustaendigeStelleService } from '../zustaendige-stelle.service';
import { ExterneFachstelleResourceSearchService } from './externe-fachstelle-resource-search.service';
import { ExterneFachstelleListLinkRel } from './externe-fachstelle.linkrel';
import { ExterneFachstelleListResource, ExterneFachstelleResource } from './externe-fachstelle.model';

@Injectable()
export class ExterneFachstelleService implements ZustaendigeStelleService<ExterneFachstelleResource> {
  private readonly searchService = inject(ExterneFachstelleResourceSearchService);

  public getSearchResultList(): Observable<InstantSearchResult<ExterneFachstelleResource>[]> {
    return this.searchService
      .getResultList()
      .pipe(
        map((externeFachstelleStateListResource: StateResource<ExterneFachstelleListResource>) =>
          this.mapListResource(externeFachstelleStateListResource),
        ),
      );
  }

  mapListResource(
    externeFachstelleStateListResource: StateResource<ExterneFachstelleListResource>,
  ): InstantSearchResult<ExterneFachstelleResource>[] {
    return this.getExterneFachstellenResources(externeFachstelleStateListResource).map(
      (externeFachstelle: ExterneFachstelleResource) => this.mapToInstantSearchResult(externeFachstelle),
    );
  }

  private getExterneFachstellenResources(
    externeFachstelleStateListResource: StateResource<ExterneFachstelleListResource>,
  ): ExterneFachstelleResource[] {
    const resources: ExterneFachstelleResource[] = getEmbeddedResources(
      externeFachstelleStateListResource,
      ExterneFachstelleListLinkRel.EXTERNE_FACHSTELLE_LIST,
    );
    return isNull(resources) ? [] : resources;
  }

  mapToInstantSearchResult(externeFachstelle: ExterneFachstelleResource): InstantSearchResult<ExterneFachstelleResource> {
    return {
      title: externeFachstelle.name,
      description: externeFachstelle.anschrift,
      data: externeFachstelle,
    };
  }

  public search(searchBy: string): void {
    this.searchService.search(searchBy);
  }

  public clearSearchResult(): void {
    this.searchService.clearResultList();
  }

  public getSelectedResult(): Observable<ExterneFachstelleResource> {
    return this.searchService.getSelectedResult();
  }

  public selectSearchResult(externeFachstelle: ExterneFachstelleResource): void {
    this.searchService.selectResult(externeFachstelle);
  }

  public clearSelectedResult(): void {
    this.searchService.clearSelectedResult();
  }
}
